// THE INVOICE EDITOR'S SAVE, AS DATA: the state the editor seeds from the
// stored invoice and lines, and the one function that turns that state into the
// body of `POST /invoices/{id}/intake-save`.
//
// Split out of `EditableInvoiceForm.tsx` (2026-09-25, `docs/decisions.md` D-181)
// so a test can build the editor's REAL payload rather than a hand-written copy
// of it. A hand-written body is how D-179 shipped refusing the app's own Import
// Control save: the test sent each line's stored total, and the editor sends a
// total it derives from price x quantity (D-4), which is not always the stored
// one. The form seeds its state with `headerStateFrom` / `lineItemStateFrom` and
// saves with `buildIntakeSaveBody`, and so do the tests.
//
// ONE BODY, AT THE ONE EDITING STEP (owner, 2026-09-29 — `docs/decisions.md`
// D-216). Import Control carries the whole header and every changed line. From
// D-174 until the merge it sent the Company and the D-128 defaults alone, while
// AP/AR Approval sent this full body; that second shape is gone with the
// Company-only rule. A line's department is sent only when the reviewer picked
// it this session (D-217) — see `departmentPicked`.

import type { Invoice, InvoiceLineItem, InvoiceLineItemWrite, IntakeSaveRequest } from './types'
import type { RouteOverrideWrite } from '../api/types'
import { lineTotal, money2, priceFromWire, priceText, quantityWire, sameDecimal } from './format'
import { lineWriteSignature } from './saveOnBlur'

export interface HeaderState {
  externalinvoiceid: string
  /** The default the box was seeded with, when intake stored no number (D-128):
      the invoice date as loaded. The server owns that default, so the box is
      sent only when the reviewer typed something else (code review, 2026-10-03). */
  externalInvoiceIdDefault?: string
  date: string
  /** DERIVED and LOCKED (owner, 2026-08-14 — `docs/decisions.md` D-3): recomputed
      from the rows on every render by `derivedSubtotal`, never typed. Kept in this
      state object anyway so the reconcile check, the field and the save payload
      all read one value. */
  subtotal: string
  tax: string
  /** The second header charge (D-1). Editable, exactly like `tax`. */
  shipping: string
  total: string
  /** NO LONGER RENDERED (owner, 2026-08-14 — D-8): the control is gone from the
      modal because a non-USD invoice is not something anyone sets here. Loaded,
      and NOT sent: an absent field leaves the stored value alone (code review
      D-07, 2026-10-03). `NonUSDCurrency` still raises and still resolves from
      the Flags panel like every other explicit flag. */
  currency: string
  vendorId?: string
  vendorName?: string
  companyId?: string
  companyName?: string
  /** The reviewer's REQUEST for manual Point Click Care entry
      (`manual_entry_requested`), as loaded. Read for the release banner; NOT
      sent — this form has no control for it any more, so sending it only
      re-sent the value it opened with (code review D-07, 2026-10-03). */
  manualEntry: boolean
}

export interface LineItemState {
  lineItemId: string
  /** The row's key on screen, kept when a line added this session takes its
      stored id, so the row is not remounted under the reviewer's caret. Absent
      on a loaded line, whose key is its id (`rowKey`). */
  clientKey?: string
  /** Concurrency counter of the stored line; absent on a line not saved yet. */
  rowVersion?: number
  sequence?: number | null
  description: string
  quantity: string
  price: string
  /** LOCKED (D-4). Always `rowTotal(row, price, quantity)`: the server's
      stored Total while Price and Quantity equal the stored ones by value,
      else `lineTotal(price, quantity)`, the server's own rule (code review
      D-02). Never sent. */
  total: string
  /** The stored line as loaded: its Price, Quantity and Total. Absent on a
      line added this session, whose Total is always worked out. */
  stored?: { price: string; quantity: string; total: string }
  /** "Account" — the trailing half. */
  accountId?: string
  accountLabel?: string
  /** "Department" — the leading half, as stored (a derived one included). Optional
      at Import Control, the one field there that is not required (D-217). */
  departmentId?: string
  departmentLabel?: string
  /** True when the reviewer picked `departmentId` this session. Only then is
      `department_id` sent: under the absent-vs-null convention an unpicked
      value would either clear a derived department (null) or pin it as
      `Manual` over the one the server re-derives for a new account. */
  departmentPicked?: boolean
  // NO account PROVENANCE and no printed-code field. Both were read only by
  // the per-line "how was this account chosen" popover, which issue 17
  // removed, and neither is ever sent — `InvoiceLineItemWrite` has no source
  // column, so the server stamps provenance itself.
  // NO PER-LINE COMPANY (owner item 5, 2026-08-17; code review B-05,
  // 2026-10-03). An invoice has one company, stored on the invoice; a line
  // has none, and the server refuses one sent on a line.
  /** True for a line the reviewer added manually and hasn't saved yet — created
      (not updated) on save, then flipped false once it has a real id. */
  isNew?: boolean
  /** COMBINE INTO ONE LINE (D-230): on a new line, the saved lines it
      replaces. Sent with the line while it is new, beside their removal. */
  combines?: string[]
  /** COMBINE (D-242): the new line's Additional Detail as chosen in the bulk
      row, sent with it while it is new. Only the sections it owes are named. */
  combineDetails?: {
    marketingCategoryId?: string | null
    transportationCategoryId?: string | null
    fleetMaintenance?: boolean | null
  }
}

/** The editor's header state for a stored invoice. The Company is the
    invoice's own `company_id` — the one field every screen reads (code review
    D-03, 2026-10-03). */
export function headerStateFrom(invoice: Invoice): HeaderState {
  return {
    // THREE FIELDS CARRY A DEFAULT WHEN INTAKE FOUND NOTHING (owner,
    // 2026-09-21 — D-128). THE SERVER OWNS THEM: the intake save fills an empty
    // field itself and does not count the fill as a correction (code review
    // E-03, 2026-10-03). They are shown here too, so the box the reviewer sees
    // holds what the save will store. `MissingExternalInvoiceId` still fires at
    // intake: the flag says the document printed no number, and the default is
    // there so nobody has to key one by hand.
    //
    // The date is used in its STORED form (`YYYY-MM-DD`), not the displayed
    // one: this value reaches the Point Click Care export and half the
    // duplicate key, and neither should move with a locale.
    externalinvoiceid: invoice.external_invoice_id ?? (invoice.invoice_date ?? '').slice(0, 10),
    ...(invoice.external_invoice_id == null
      ? { externalInvoiceIdDefault: (invoice.invoice_date ?? '').slice(0, 10) }
      : {}),
    date: invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : '',
    subtotal: invoice.subtotal != null ? money2(String(invoice.subtotal)) : '',
    // Tax and Shipping mean ZERO when the document shows neither, and an empty
    // box would otherwise block the release as a missing required field.
    tax: invoice.tax != null ? money2(String(invoice.tax)) : '0.00',
    shipping: invoice.shipping != null ? money2(String(invoice.shipping)) : '0.00',
    total: invoice.total != null ? money2(String(invoice.total)) : '',
    currency: invoice.currency ?? 'USD',
    vendorId: invoice.vendor_id || undefined,
    vendorName: invoice.vendor_name || undefined,
    companyId: invoice.company_id || undefined,
    companyName: invoice.company_name || undefined,
    manualEntry: invoice.manual_entry_requested ?? false,
  }
}

/** The editor's row state for a stored line: the seed, and the reseed after a
    save. */
export function lineItemStateFrom(c: InvoiceLineItem): LineItemState {
  const price = priceFromWire(c.unit_price)
  const quantity = c.quantity != null ? String(c.quantity) : ''
  const total = c.total != null ? money2(String(c.total)) : ''
  return {
    lineItemId: c.id,
    rowVersion: c.row_version,
    sequence: c.line_number,
    description: c.description ?? '',
    quantity,
    // EXACT, every decimal kept (D-249): $0.0049 opens as 0.0049, not 0.00.
    price,
    // THE STORED TOTAL, as the server derived it (code review D-02). The
    // editor re-derived every Total on open, so a client rule that drifted
    // from the server's showed a "doesn't reconcile" no flag stood behind
    // (D-249). It derives only on an edit, with the server's own rule.
    total,
    stored: { price, quantity, total },
    accountId: c.account_id || undefined,
    // The stored department, re-derived by the server after an account
    // change; a fresh read is never the reviewer's own pick (D-217).
    departmentId: c.department_id || undefined,
  }
}

/**
 * A ROW'S TOTAL for the Price and Quantity it now holds (code review D-02). The
 * stored Total while both equal the stored ones BY VALUE — touching a box, or
 * typing `6066.0000` over `6066`, changes nothing — and `lineTotal` only once
 * one of them really differs. A line added this session has no stored Total.
 */
export function rowTotal(row: Pick<LineItemState, 'stored'>, price: string, quantity: string): string {
  const s = row.stored
  if (s && sameDecimal(price, s.price) && sameDecimal(quantity, s.quantity)) return s.total
  return lineTotal(price, quantity)
}

/**
 * The last-synced write signature of each stored line, keyed by its id — what
 * `changedLineIds` compares the rows against to decide which a save sends.
 * Seeded from the LOADED lines, so the first save compares against what the
 * server already has rather than counting every row as changed.
 */
export function lineChangeBaselineFrom(lineItems: readonly InvoiceLineItem[]): Map<string, string> {
  return new Map(
    lineItems.map((c) => [
      c.id,
      lineWriteSignature({
        lineItemId: c.id,
        sequence: c.line_number,
        description: c.description ?? '',
        accountId: c.account_id || undefined,
        quantity: c.quantity != null ? String(c.quantity) : '',
        price: priceFromWire(c.unit_price),
      }),
    ]),
  )
}

/** A row's key on screen and in the focus keys (`description:<key>`): stable
    across the save that gives a new line its id. */
export function rowKey(r: Pick<LineItemState, 'lineItemId' | 'clientKey'>): string {
  return r.clientKey ?? r.lineItemId
}

interface IntakeSaveBodyInput {
  header: HeaderState
  rows: readonly LineItemState[]
  /** The rows whose write signature moved (`changedLineIds`); only these are sent. */
  changedIds: ReadonlySet<string>
  rowVersion: number
  /** The line's current counter, when the caller holds a fresher one than the row state. */
  lineRowVersion: (lineItemId: string) => number | undefined
  removed: readonly string[]
  release: boolean
  /** The Override pop-up's answer, `null` to clear it; absent leaves the
      stored Override alone (D-350). */
  routeOverride?: RouteOverrideWrite | null
}

/** A combined line's chosen Additional Detail (D-242), in the wire's names. */
function combineDetailFields(
  d: NonNullable<LineItemState['combineDetails']>,
): Pick<InvoiceLineItemWrite, 'marketing_category_id' | 'transportation_category_id' | 'fleet_maintenance'> {
  return {
    ...(d.marketingCategoryId !== undefined ? { marketing_category_id: d.marketingCategoryId } : {}),
    ...(d.transportationCategoryId !== undefined ? { transportation_category_id: d.transportationCategoryId } : {}),
    ...(d.fleetMaintenance !== undefined ? { fleet_maintenance: d.fleetMaintenance } : {}),
  }
}

/** Whether the External Invoice ID box holds something the reviewer typed,
    rather than the default it was seeded with (or the current date's default). */
export function externalIdTyped(header: HeaderState): boolean {
  if (header.externalInvoiceIdDefault === undefined) return true
  const v = header.externalinvoiceid.trim()
  return v !== '' && v !== header.externalInvoiceIdDefault && v !== header.date.trim()
}

/** The intake-save body the editor sends for `input`. */
export function buildIntakeSaveBody(input: IntakeSaveBodyInput): IntakeSaveRequest {
  const { header } = input
  // A blank field CLEARS the stored column: the request is PATCH-shaped, where
  // an absent key means "leave alone" and null means "clear".
  const textOrNull = (v: string): string | null => (v.trim() !== '' ? v.trim() : null)
  // MONEY IS A DECIMAL STRING ON THE WIRE (docs/api.md's global conventions):
  // the server's `field.money` refuses a JSON number outright.
  const decimalOrNull = (v: string): string | null => (v.trim() !== '' ? money2(v) : null)
  // A QUANTITY IS NOT MONEY (code review E-03): `numeric(14,4)`, so `money2`
  // turned 0.125 into 0.13 and changed the line's total. Sent at four places
  // (`quantityWire`), and the server compares it by value.
  const quantityOrNull = (v: string): string | null => quantityWire(v)

  const changed = input.rows.filter((r) => input.changedIds.has(r.lineItemId))
  // No id = create. With an id the line's own concurrency counter rides along,
  // so an edit to a line someone else just changed is refused rather than
  // silently winning.
  const identity = (r: LineItemState): Pick<InvoiceLineItemWrite, 'id' | 'row_version'> =>
    r.isNew ? {} : { id: r.lineItemId, row_version: input.lineRowVersion(r.lineItemId) ?? r.rowVersion }
  const upsert: InvoiceLineItemWrite[] = changed.map((r) => ({
    ...identity(r),
    // Every saved line carries its number, and a new one is numbered when added.
    line_number: r.sequence as number,
    description: textOrNull(r.description),
    account_id: r.accountId ?? null,
    // THE DEPARTMENT ONLY WHEN PICKED (D-217). Absent otherwise: sending the
    // stored value would be ignored, but sending null would CLEAR a
    // `ChartImplied` department on every save.
    ...(r.departmentPicked ? { department_id: r.departmentId ?? null } : {}),
    // The lines a combined line replaces (D-230), only while it is new.
    ...(r.isNew && r.combines && r.combines.length > 0 ? { combines: [...r.combines] } : {}),
    // And the Additional Detail chosen for it in the bulk row (D-242): each
    // one named is sent, a blank as null; one not named is left to the carry.
    ...(r.isNew && r.combines && r.combines.length > 0 && r.combineDetails
      ? combineDetailFields(r.combineDetails)
      : {}),
    quantity: quantityOrNull(r.quantity),
    // The price exactly as it stands (D-249) — never through `money2`.
    unit_price: r.price.trim() !== '' ? priceText(r.price) : null,
    // NO `total`: the server derives every line total from price x quantity
    // (D-249) and ignored the one sent (code review D-07).
  }))
  return {
    row_version: input.rowVersion,
    invoice: {
      // ONLY WHEN TYPED (code review, 2026-10-03). An empty External Invoice ID
      // takes the invoice date on the server (D-128), from the date this save
      // leaves in place; sending the seeded default would pin the OLD date when
      // the reviewer corrects the date in the same save.
      ...(externalIdTyped(header) ? { external_invoice_id: textOrNull(header.externalinvoiceid) } : {}),
      invoice_date: textOrNull(header.date),
      // NO `subtotal` (the server derives it from the saved lines, D-3), NO
      // `currency` (no control, D-8; absent leaves it alone) and NO
      // `manual_entry_requested` (no control on this form) — code review D-07.
      tax: decimalOrNull(header.tax),
      shipping: decimalOrNull(header.shipping),
      total: decimalOrNull(header.total),
      vendor_id: header.vendorId ?? null,
      // The reviewer's Company — changeable here, the one step that settles it.
      // The ids only: the server reads both NAMES from the masters, so the app
      // sends neither (code review F-08, 2026-10-03).
      company_id: header.companyId ?? null,
    },
    line_items: { upsert, remove: [...input.removed] },
    release: input.release,
    ...(input.routeOverride === undefined ? {} : { route_override: input.routeOverride }),
  }
}
